import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
    forgotPassword,
    resetPassword,
    clearPasswordResetSuccess,
    resetOtpState,
    verifyResetPasswordOtp,
} from '../../redux/AuthSlice/authSlice';
import { Eye, EyeOff, Lock, Mail, Clock, HelpCircle, AlertCircle, Search, Handshake, BarChart3 } from 'lucide-react';
import CareerChampWordmark from '../../components/Brand/CareerChampWordmark';

const PRIMARY = '#3F57E0';
const PRIMARY_50 = '#EEF1FE';

const FEATURES = [
    { icon: <Search size={15} />, text: 'Discover companies about to hire — before jobs go public' },
    { icon: <Handshake size={15} />, text: 'Get insider referrals straight to the hiring manager' },
    { icon: <BarChart3 size={15} />, text: 'Track every conversation in one place' },
];

const RULES = [
    { label: 'At least 8 characters',   test: p => p.length >= 8 },
    { label: 'Upper & lowercase',        test: p => /[A-Z]/.test(p) && /[a-z]/.test(p) },
    { label: 'One number',               test: p => /\d/.test(p) },
    { label: 'One symbol (!@#$…)',       test: p => /[^A-Za-z0-9]/.test(p) },
];
const STRENGTH_COLORS = ['#F97066', '#FDB022', PRIMARY, '#10B981'];

/* ── Shared brand panel — same across all auth screens ── */
function BrandPanel() {
    return (
        <div
            className="hidden md:flex md:w-1/2 min-h-screen flex-col p-14"
            style={{ background: 'linear-gradient(155deg, #1A2454 0%, #3F57E0 55%, #6B7CF5 100%)' }}
        >
            {/* Logo — top-left */}
            <div className="flex items-center gap-3">
                <div style={{ width: 44, height: 44, borderRadius: 13, background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <span style={{ fontFamily: "'Barlow Condensed', 'Arial Narrow', sans-serif", fontWeight: 800, fontStyle: 'italic', color: 'white', fontSize: 17 }}>CC</span>
                </div>
                <CareerChampWordmark variant="light" className="text-[30px]" />
            </div>

            {/* Spacer — ~22vh keeps headline in the left-lower area on all screen sizes */}
            <div style={{ height: 'calc(100vh * 0.27)', flexShrink: 0 }} />

            {/* Headline + features — left-anchored */}
            <div className="mb-auto text-left">
                <h1 className="text-4xl font-bold text-white leading-tight mb-4">
                    Skip the Resume<br />Black Hole
                </h1>
                <p className="text-blue-200 text-sm mb-8 leading-relaxed" style={{ maxWidth: 360 }}>
                    Get referred to hiring managers at companies about to hire — before the job is even posted.
                </p>
                <div className="space-y-4">
                    {FEATURES.map((f, i) => (
                        <div key={i} className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white flex-shrink-0"
                                style={{ backgroundColor: 'rgba(255,255,255,0.15)' }}>
                                {f.icon}
                            </div>
                            <span className="text-blue-100 text-sm">{f.text}</span>
                        </div>
                    ))}
                </div>
            </div>

        </div>
    );
}

/* ── OTP boxes ── */
function OtpBoxes({ value, onChange }) {
    const refs = useRef([]);
    const digits = value.padEnd(6, ' ').split('').map(c => c === ' ' ? '' : c);

    const update = (idx, char) => {
        const next = [...digits];
        next[idx] = char;
        onChange(next.join('').trimEnd());
        if (char && idx < 5) refs.current[idx + 1]?.focus();
    };

    const handleKeyDown = (idx, e) => {
        if (e.key === 'Backspace') {
            if (digits[idx]) { update(idx, ''); }
            else if (idx > 0) { refs.current[idx - 1]?.focus(); update(idx - 1, ''); }
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
        onChange(pasted);
        refs.current[Math.min(pasted.length, 5)]?.focus();
    };

    return (
        <div className="flex gap-3">
            {digits.map((d, i) => (
                <input key={i} ref={el => refs.current[i] = el}
                    type="text" inputMode="numeric" maxLength={1} value={d}
                    onChange={e => { const c = e.target.value.replace(/\D/g, '').slice(-1); update(i, c); }}
                    onKeyDown={e => handleKeyDown(i, e)} onPaste={handlePaste}
                    className="w-12 h-14 text-center text-2xl font-bold rounded-xl outline-none transition-all"
                    style={{
                        border: d ? `2px solid ${PRIMARY}` : '1px solid #E6E8EE',
                        background: d ? PRIMARY_50 : 'white',
                        color: d ? PRIMARY : '#94A3B8',
                        boxShadow: '0 1px 2px rgba(15,23,42,0.06)',
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                    }} />
            ))}
        </div>
    );
}

function ErrorBanner({ message }) {
    return (
        <div className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <p className="leading-5">{message}</p>
        </div>
    );
}

/* ═══════════════════════════════════════════════════════ */
const ForgotPass = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { loading, error, otpVerified, verifiedEmail } = useSelector(s => s.auth);

    const [email, setEmail] = useState('');
    const [otp, setOtp] = useState('');
    const [newPass, setNewPass] = useState('');
    const [confirmPass, setConfirmPass] = useState('');
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [step, setStep] = useState(1);
    const [otpTimer, setOtpTimer] = useState(0);
    const [isTimerActive, setIsTimerActive] = useState(false);
    const [isVerifying, setIsVerifying] = useState(false);
    const [localError, setLocalError] = useState('');

    useEffect(() => {
        dispatch(clearPasswordResetSuccess());
        dispatch(resetOtpState());
        return () => { dispatch(clearPasswordResetSuccess()); dispatch(resetOtpState()); };
    }, [dispatch]);

    useEffect(() => {
        if (!isTimerActive || otpTimer <= 0) { setIsTimerActive(false); return; }
        const t = setInterval(() => setOtpTimer(p => p - 1), 1000);
        return () => clearInterval(t);
    }, [otpTimer, isTimerActive]);

    const handleRequestOtp = async (e) => {
        e?.preventDefault();
        setLocalError('');
        if (!email || !email.includes('@')) { setLocalError('Please enter a valid email address.'); return; }
        if (isTimerActive) return;
        const result = await dispatch(forgotPassword(email));
        if (forgotPassword.fulfilled.match(result)) {
            setStep(2); setOtpTimer(30); setIsTimerActive(true);
        } else {
            setLocalError(result.payload || 'Failed to send code. Please try again.');
        }
    };

    const handleVerifyOtp = async () => {
        setLocalError('');
        if (otp.length < 6) { setLocalError('Please enter all 6 digits.'); return; }
        setIsVerifying(true);
        const result = await dispatch(verifyResetPasswordOtp({ email, otp }));
        setIsVerifying(false);
        if (verifyResetPasswordOtp.fulfilled.match(result)) { setStep(3); }
        else { setLocalError(result.payload || 'Invalid code. Please try again.'); }
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        setLocalError('');
        if (!newPass || newPass.length < 8) { setLocalError('Password must be at least 8 characters.'); return; }
        if (newPass !== confirmPass) { setLocalError('Passwords do not match.'); return; }
        if (!otpVerified || verifiedEmail !== email) { setLocalError('Session expired. Please start over.'); return; }
        const result = await dispatch(resetPassword({ email, otp, newPassword: newPass }));
        if (resetPassword.fulfilled.match(result)) { navigate('/login'); }
        else { setLocalError(result.payload || 'Failed to reset password. Please try again.'); }
    };

    const errorMsg = localError || error;
    const strengthScore = RULES.filter(r => r.test(newPass)).length;

    const formPanel = (topRight, children) => (
        <div className="h-screen overflow-y-auto flex flex-col md:flex-row">
            <BrandPanel />
            <div className="w-full md:w-1/2 min-h-screen flex flex-col bg-gray-50">
                <div className="flex justify-end p-6">{topRight}</div>
                <div className="flex-1 flex items-center justify-center px-8 pb-8">
                    <div className="w-full max-w-md text-left">{children}</div>
                </div>
            </div>
        </div>
    );

    /* ── Step 1: request OTP ── */
    if (step === 1) return formPanel(
        <button type="button" onClick={() => navigate('/login')}
            className="text-sm text-gray-500 hover:text-gray-700 transition-colors">
            Remembered it?{' '}
            <span className="font-semibold" style={{ color: PRIMARY }}>Back to sign in →</span>
        </button>,
        <div>
            <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5"
                style={{ background: PRIMARY_50 }}>
                <Lock className="h-5 w-5" style={{ color: PRIMARY }} />
            </div>
            <div className="mb-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-1">Forgot your password?</h2>
                <p className="text-gray-500 text-sm">
                    No worries — enter the email tied to your Career Champ account and we'll send a secure reset code.
                </p>
            </div>
            <form onSubmit={handleRequestOtp} className="space-y-4">
                <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">Email</label>
                    <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                            <Mail className="h-4 w-4 text-gray-400" />
                        </div>
                        <input type="email"
                            className="block w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-xl bg-white shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent text-sm"
                            placeholder="you@company.com"
                            value={email} onChange={e => setEmail(e.target.value)} required
                            disabled={loading || isTimerActive} />
                    </div>
                </div>
                {errorMsg && <ErrorBanner message={errorMsg} />}
                <button type="submit" disabled={loading || !email || isTimerActive}
                    className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
                    style={{ backgroundColor: PRIMARY }}>
                    {loading ? 'Sending…' : 'Send reset link →'}
                </button>
            </form>
            <div className="mt-5 p-3.5 rounded-xl border flex gap-2.5 items-start text-xs text-gray-500 leading-relaxed"
                style={{ background: '#F6F7FB', borderColor: '#E6E8EE' }}>
                <HelpCircle className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                <span>
                    The link is valid for <b className="text-gray-700">15 minutes</b>. If you signed in with Google or LinkedIn, use that provider instead — there's no password to reset.
                </span>
            </div>
            <div className="mt-5 text-center text-xs text-gray-500">
                Still need help?{' '}
                <button type="button" onClick={() => navigate('/support')}
                    className="font-semibold" style={{ color: PRIMARY }}>
                    Contact support →
                </button>
            </div>
        </div>
    );

    /* ── Step 2: OTP verification ── */
    if (step === 2) return formPanel(
        <button type="button" onClick={() => { setStep(1); setOtp(''); setLocalError(''); }}
            className="text-sm text-gray-500 hover:text-gray-700 transition-colors">
            Wrong email?{' '}
            <span className="font-semibold" style={{ color: PRIMARY }}>Change it →</span>
        </button>,
        <div>
            <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5"
                style={{ background: PRIMARY_50 }}>
                <Mail className="h-5 w-5" style={{ color: PRIMARY }} />
            </div>
            <div className="mb-6">
                <h2 className="text-3xl font-bold text-gray-900 mb-1">Check your email</h2>
                <p className="text-gray-500 text-sm">
                    We sent a 6-digit code to{' '}
                    <span className="font-semibold text-gray-800">{email}</span>. It expires in 15 minutes.
                </p>
            </div>
            <OtpBoxes value={otp} onChange={setOtp} />
            <div className="flex items-center gap-1.5 mt-3 text-xs text-gray-500">
                <Clock className="h-3 w-3 flex-shrink-0" />
                Didn't get it?{' '}
                <button type="button" onClick={handleRequestOtp} disabled={isTimerActive || loading}
                    className="font-semibold disabled:text-gray-400"
                    style={{ color: isTimerActive ? undefined : PRIMARY }}>
                    {isTimerActive ? `Resend in ${otpTimer}s` : 'Resend code'}
                </button>
            </div>
            {errorMsg && <div className="mt-4"><ErrorBanner message={errorMsg} /></div>}
            <button type="button" onClick={handleVerifyOtp} disabled={isVerifying || otp.length < 6 || loading}
                className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl text-sm font-semibold text-white mt-6 transition-opacity hover:opacity-90 disabled:opacity-50"
                style={{ backgroundColor: PRIMARY }}>
                {isVerifying ? 'Verifying…' : 'Verify and continue →'}
            </button>
            <div className="mt-5 p-3.5 rounded-xl border flex gap-2.5 items-start text-xs text-gray-500 leading-relaxed"
                style={{ background: '#F6F7FB', borderColor: '#E6E8EE' }}>
                <HelpCircle className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                <span>Can't find it? Check your spam folder. We'll never ask for your code over chat or email.</span>
            </div>
        </div>
    );

    /* ── Step 3: new password ── */
    return formPanel(
        <button type="button" onClick={() => navigate('/login')}
            className="text-sm text-gray-500 hover:text-gray-700 transition-colors">
            <span className="font-semibold" style={{ color: PRIMARY }}>Back to sign in →</span>
        </button>,
        <div>
            <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5"
                style={{ background: '#ECFDF3' }}>
                <svg className="h-5 w-5" style={{ color: '#067647' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
            </div>
            <div className="mb-6">
                <h2 className="text-3xl font-bold text-gray-900 mb-1">Set a new password</h2>
                <p className="text-gray-500 text-sm">
                    Resetting for <span className="font-semibold text-gray-800">{email}</span>. Pick something memorable but strong.
                </p>
            </div>
            <form onSubmit={handleResetPassword} className="space-y-4">
                <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">New password</label>
                    <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                            <Lock className="h-4 w-4 text-gray-400" />
                        </div>
                        <input type={showNew ? 'text' : 'password'}
                            className="block w-full pl-10 pr-10 py-2.5 border border-gray-200 rounded-xl bg-white shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent text-sm"
                            placeholder="At least 8 characters" value={newPass}
                            onChange={e => setNewPass(e.target.value)} required disabled={loading} />
                        <button type="button" onClick={() => setShowNew(!showNew)}
                            className="absolute inset-y-0 right-0 pr-3.5 flex items-center">
                            {showNew ? <EyeOff className="h-4 w-4 text-gray-400" /> : <Eye className="h-4 w-4 text-gray-400" />}
                        </button>
                    </div>
                </div>
                <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">Confirm password</label>
                    <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                            <Lock className="h-4 w-4 text-gray-400" />
                        </div>
                        <input type={showConfirm ? 'text' : 'password'}
                            className="block w-full pl-10 pr-10 py-2.5 border border-gray-200 rounded-xl bg-white shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent text-sm"
                            placeholder="Confirm your password" value={confirmPass}
                            onChange={e => setConfirmPass(e.target.value)} required disabled={loading} />
                        <button type="button" onClick={() => setShowConfirm(!showConfirm)}
                            className="absolute inset-y-0 right-0 pr-3.5 flex items-center">
                            {showConfirm ? <EyeOff className="h-4 w-4 text-gray-400" /> : <Eye className="h-4 w-4 text-gray-400" />}
                        </button>
                    </div>
                </div>

                {/* Strength meter */}
                {newPass && (
                    <div>
                        <div className="flex gap-1 mb-2">
                            {[1, 2, 3, 4].map(i => (
                                <div key={i} className="flex-1 h-1 rounded-full transition-all"
                                    style={{ background: i <= strengthScore ? STRENGTH_COLORS[strengthScore - 1] : '#E6E8EE' }} />
                            ))}
                        </div>
                        <div className="grid grid-cols-2 gap-1.5">
                            {RULES.map((r, i) => {
                                const ok = r.test(newPass);
                                return (
                                    <div key={i} className="flex items-center gap-1.5 text-xs font-medium"
                                        style={{ color: ok ? '#067647' : '#94A3B8' }}>
                                        <span className="w-3.5 h-3.5 rounded-full flex-shrink-0 flex items-center justify-center"
                                            style={{ background: ok ? '#ECFDF3' : '#F6F7FB', border: `1px solid ${ok ? '#ABEFC6' : '#E6E8EE'}` }}>
                                            {ok
                                                ? <svg className="w-2 h-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                                                : <span className="w-1.5 h-1.5 rounded-full bg-gray-300" />}
                                        </span>
                                        {r.label}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {errorMsg && <ErrorBanner message={errorMsg} />}

                <button type="submit" disabled={loading || !newPass || !confirmPass}
                    className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
                    style={{ backgroundColor: PRIMARY }}>
                    {loading ? 'Updating…' : 'Update password →'}
                </button>
            </form>
            <div className="mt-5 p-3.5 rounded-xl border flex gap-2.5 items-start text-xs text-gray-500 leading-relaxed"
                style={{ background: '#F6F7FB', borderColor: '#E6E8EE' }}>
                <HelpCircle className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                <span>After updating, you'll be redirected to sign in.</span>
            </div>
        </div>
    );
};

export default ForgotPass;
